import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'reducers';
import { transcriptChunkStore } from '../MediaPlayer/Transcript/TranscriptChunkStore';

type Options = {
  isFetching?: boolean;
};

const VIEW_MODE_SWITCH_TIMEOUT_MS = 8000;

/** Locks the caption/paragraph View toggle while transcript data loads or remounts. */
export const useViewModeToggleLock = ({ isFetching = false }: Options = {}) => {
  const player = useSelector((state: RootState) => state.player);
  const [isViewModeSwitching, setIsViewModeSwitching] = useState(false);
  const switchSessionRef = useRef(0);
  /** Sync lock — Redux can re-render before React state commits isViewModeSwitching. */
  const viewSwitchLockRef = useRef(false);

  const isTranscriptLoading =
    player.isProcessingData || !player.loadCompleted || isFetching;

  const isViewToggleBusy =
    viewSwitchLockRef.current ||
    isViewModeSwitching ||
    isTranscriptLoading;

  const releaseSwitch = useCallback((sessionId: number) => {
    if (switchSessionRef.current !== sessionId) return;
    viewSwitchLockRef.current = false;
    setIsViewModeSwitching(false);
  }, []);

  const beginViewModeSwitch = useCallback(
    (runSwitch: () => void) => {
      if (isViewToggleBusy) return false;

      const sessionId = switchSessionRef.current + 1;
      switchSessionRef.current = sessionId;
      viewSwitchLockRef.current = true;
      setIsViewModeSwitching(true);
      runSwitch();
      return true;
    },
    [isViewToggleBusy],
  );

  useEffect(() => {
    if (!isViewModeSwitching) return;

    if (isTranscriptLoading) return;

    const sessionId = switchSessionRef.current;

    if (!transcriptChunkStore.isEnabled()) {
      releaseSwitch(sessionId);
      return;
    }

    let sawNotReady = !transcriptChunkStore.getSnapshot().editorReady;
    const timeoutId = window.setTimeout(
      () => releaseSwitch(sessionId),
      VIEW_MODE_SWITCH_TIMEOUT_MS,
    );

    let unsubscribe: (() => void) | undefined;
    const onStoreChange = () => {
      const snap = transcriptChunkStore.getSnapshot();
      if (!snap.editorReady) {
        sawNotReady = true;
        return;
      }
      if (sawNotReady && snap.loadingChunkId == null) {
        window.clearTimeout(timeoutId);
        unsubscribe?.();
        releaseSwitch(sessionId);
      }
    };

    unsubscribe = transcriptChunkStore.subscribe(onStoreChange);

    const syncFallbackId = window.setTimeout(() => {
      if (!sawNotReady) {
        window.clearTimeout(timeoutId);
        unsubscribe?.();
        releaseSwitch(sessionId);
      }
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(syncFallbackId);
      unsubscribe?.();
    };
  }, [
    isViewModeSwitching,
    isTranscriptLoading,
    player.isParagraphMode,
    releaseSwitch,
  ]);

  return {
    isViewToggleBusy,
    isViewModeSwitching,
    isTranscriptLoading,
    beginViewModeSwitch,
  };
};
